import React, { useMemo, useState, useCallback } from "react";
import styled from "styled-components";
import ServiceLocator from "../../../../../domain/service-locator";
import { MinpakuHeader } from "../../components/header/minpaku.header";
import { MinpakuLocalMap } from "./minpaku-local-map";
import AnimatedPageTransition from "../../../../../domain/module/navigation/animated-page-transition";

// エリアごとのアクティブ色
const AREA_COLORS = ["#EF834A", "#84B4A8", "#8697C1"];
const INACTIVE_COLOR = "#CCCCCC";

/**
 * エリアローカルマップ
 * エリアの localMap.mapUrl と localMap.pins を使用
 */
export const MinpakuMapPage = () => {
  /**
   * Prepare Modules
   */
  const localizedEntityRepository = ServiceLocator.LocalizedEntityRepository;

  /**
   * watch ReactiveProperty
   */
  const entities = localizedEntityRepository.entities.watch();

  /**
   * useState
   */
  const [selectedAreaIndex, setSelectedAreaIndex] = useState(0);

  /**
   * useMemo
   */
  // localMapを持つエリアのみをフィルタ
  const areasWithLocalMap = useMemo(() => {
    return entities.areas.filter(
      (area) => area.localMap && area.localMap.mapUrl
    );
  }, [entities.areas]);

  /**
   * useCallback
   */
  const onSelectArea = useCallback((index: number) => {
    setSelectedAreaIndex(index);
  }, []);

  if (areasWithLocalMap.length === 0) {
    return (
      <AnimatedPageTransition>
        <div className={"h-[100vh] bg-white"}>
          <MinpakuHeader />
          <div className="flex items-center justify-center h-full">
            <p>マップが設定されていません</p>
          </div>
        </div>
      </AnimatedPageTransition>
    );
  }

  return (
    <AnimatedPageTransition>
      <div className={"h-[100vh] bg-white flex flex-col"}>
        <MinpakuHeader />
        <AreaSelector>
          {areasWithLocalMap.map((area, index) => (
            <AreaButton
              key={area.id}
              isActive={index === selectedAreaIndex}
              activeColor={AREA_COLORS[index % AREA_COLORS.length]}
              onClick={() => onSelectArea(index)}
            >
              {`${String.fromCharCode(65 + index)}ブロック`}
            </AreaButton>
          ))}
        </AreaSelector>
        <MapContainer>
          {/* 全エリアのマップを重ねて配置し、選択されたもののみ表示 */}
          {areasWithLocalMap.map((area, index) => {
            const areaTours = entities.tours.filter((x) => x.areaId === area.id);
            const areaGuides = entities.guides.filter((x) =>
              areaTours.map((t) => t.id).includes(x.tourId)
            );
            const areaPinPositions = new Map(
              (area.localMap?.pins || []).map((pin) => [
                pin.guideId,
                { x: pin.x, y: pin.y },
              ])
            );
            return (
              <MapLayer
                key={area.id}
                isVisible={index === selectedAreaIndex}
              >
                {area.localMap && (
                  <MinpakuLocalMap
                    guides={areaGuides}
                    mapImageUrl={area.localMap.mapUrl}
                    pinPositions={areaPinPositions}
                    pinColor={AREA_COLORS[index % AREA_COLORS.length]}
                  />
                )}
              </MapLayer>
            );
          })}
        </MapContainer>
      </div>
    </AnimatedPageTransition>
  );
};

const AreaSelector = styled.div`
  display: flex;
  padding: 0 8px;
  gap: 4px;
  margin-top: 12px;
`;

const AreaButton = styled.button<{ isActive: boolean; activeColor: string }>`
  flex: 1;
  padding: 12px 8px;
  font-size: 14px;
  font-weight: bold;
  border: none;
  z-index: 100;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.isActive ? props.activeColor : INACTIVE_COLOR};
  color: ${(props) => (props.isActive ? "#FFFFFF" : "#666666")};
  box-shadow: ${(props) =>
    props.isActive
      ? "0 4px 8px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.15)"
      : "0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1)"};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25), 0 3px 6px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

const MapContainer = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
`;

const MapLayer = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.isVisible ? "auto" : "none")};
  transition: opacity 0.2s ease;
`;
